import { useCallback, useMemo, useState } from 'react'
import { DictionaryComponents } from '../../../../utils/globals/types'
import { ITInputNumeric, ITInputPrice, ITRectangularInputSmall } from '../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Inputs'
import { ITDisplayPercent, ITDisplayPrice } from '../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { ColumnConfiguration } from '../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { DeleteRow } from '../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import VatTableChildren, { VatTableChildrenNew } from './VatTableChildren'
import { useOnChangeVatLayoutTableLogic, useVatLayoutTableRender } from './VatTableChildren/useVatLayoutTableHooks'
import { ColumnITLayoutTableConfiguration, ITComponentElement, ITHookFunctionTy, ITLayoutTableModalOpen, OnChangeElements } from '../../../../baseComponents/TwinTable/InputTableLayout/InputTable/types'
import { TableWithoutVirtualListingContainerRenderer, TableWithoutVirtualListingRowProps } from '../../../../baseComponents/TwinTable/TableWithoutVirtualListing/types'
import { TableWithoutDefaultRowRenderer } from '../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import VatTableLayout, { VatTableLayoutNew, VatTableLayoutProps } from './VatTableLayout'
import AppStyleDecider from '../../../../baseComponents/AppsStyleDecider'

interface VatLayoutProps extends VatTableLayoutProps {}

const VatLayout: React.FC<VatLayoutProps> = ({ ...rest }) => {
    return (
        <AppStyleDecider DefaultComponent={VatLayoutDefault} NewComponent={VatLayoutNew} {...rest} />
    )
}

const VatLayoutDefault: React.FC<VatLayoutProps> = ({ ...rest }) => {
    return (
        <VatTableLayout hookFunction={useVatLayoutTableLogic} TableChildren={VatTableChildren} {...rest} />
    )
}

const VatLayoutNew: React.FC<VatLayoutProps> = ({ ...rest }) => {
    return (
        <VatTableLayoutNew hookFunction={useVatLayoutTableLogic} TableChildren={VatTableChildrenNew} {...rest} />
    )
}

const components: DictionaryComponents = {
    ITRectangularInput: ITRectangularInputSmall,
    ITInputPrice: ITInputPrice,
    ITInputNumeric: ITInputNumeric,
    ITDisplayPrice: ITDisplayPrice,
    ITDisplayPercent: ITDisplayPercent
}

const useVatLayoutTableLogic: ITHookFunctionTy = ({ rowRenderer, columns, CurrencyId, setCompleteData, inputTableURLs, getCompleteData, completeData, ...rest }) => {
    const [openModal, setOpenModal] = useState<ITLayoutTableModalOpen>(null)
    const { onChangeTotals, onChangeFetch, onChangeTotalsFetch, onChange, onAddNewLine, onChangeUpdate, deleteLine } = useOnChangeVatLayoutTableLogic({ setCompleteData, inputTableURLs, getCompleteData, ...rest })
    const { addButtonRowRender } = useVatLayoutTableRender()
    
    const handleProductLineTypes = useCallback((props: TableWithoutVirtualListingRowProps) => {
        if (props?.rowData?.type === 'addButton') {
            props.key = 'addButton'
            return addButtonRowRender(props, onAddNewLine)
        }
        return <TableWithoutDefaultRowRenderer {...props} className={(props.className || '')} key={props.rowData?.id || props.rowIndex} />
        
    }, [addButtonRowRender, onAddNewLine])

    const addCustomRender = useCallback((columns: ITComponentElement<any>[]) => {
        const onChanges: OnChangeElements = {
            onChange: onChange,
            onChangeFetch: onChangeFetch,
            onChangeTotals: onChangeTotals,
            onChangeTotalsFetch: onChangeTotalsFetch,
        }
        let result: ColumnConfiguration[] = []

        for (const key in columns) {
            let column = columns[key]
            const myElement = column as ColumnConfiguration
            if (column.component) {
                const Element = components[column.component]
                const onChange = onChanges[column?.typeOnChange || 'onChangeFetch']
                if (column.component === 'ITDeleteRow') {
                    myElement.customRender = (parameterValue: any, allRowData: any) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(id, allRowData) => id ? setOpenModal({ type: 'deleteLine', allRowData }) : deleteLine(allRowData.id)} />
                } else {
                    myElement.customRender = (value, allRowData) => <Element {...column as any} {...{ value, allRowData, onChange, CurrencyId, setOpenModal }} />
                }
            }
            result.push(myElement)
        }
        return result

    }, [CurrencyId, onChange, onChangeFetch, onChangeTotals, onChangeTotalsFetch, setOpenModal, deleteLine])

    const parseColumns = useCallback((columns: ColumnITLayoutTableConfiguration[]) => {
        let result = addCustomRender(columns as any)
        return result
    }, [addCustomRender])

    const parsedColumns = useMemo(() => parseColumns(columns), [columns, parseColumns])

    const containerRenderer: TableWithoutVirtualListingContainerRenderer = useCallback((props) => {
        return (
            <div className='relative'>
                {props.children}
            </div>
        )
    }, [])

    return { parsedColumns, rowRenderer: rowRenderer || handleProductLineTypes, setOpenModal, openModal, eventFunctions: { deleteLine, onChangeAdd: () => { }, onChangeUpdate, changeOrder: () => { }, changeSingleField: () => { }, onChange, onChangeFetch }, containerRenderer, parsedTableData: completeData?.tableData, onChangeSearchBar: () => {}, addCustomRender }
}

export default VatLayout